import { Head, useForm } from '@inertiajs/react'
import MainLayout from '../components/layout/MainLayout'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Textarea from '../components/ui/Textarea'
import Card from '../components/ui/Card'
import { useState } from 'react'

/**
 * Contact page - Formulaire de contact et FAQ
 */
export default function Contact() {
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    email: '',
    phone: '',
    message: ''
  })

  const [openFaq, setOpenFaq] = useState(null)

  const handleSubmit = (e) => {
    e.preventDefault()
    post('/contact')
  }

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  const faqs = [
    {
      question: 'Comment puis-je commencer avec SmartImmo ?',
      answer: 'Créez simplement votre compte gratuitement sur notre plateforme. Vous pourrez ensuite générer votre QR code personnalisé et commencer à recevoir des leads immédiatement.'
    },
    {
      question: 'Combien de temps faut-il pour recevoir mon premier lead ?',
      answer: 'Dès que votre QR code est scanné et qu\'un visiteur complète la conversation avec notre IA, vous recevez le lead instantanément dans votre dashboard. Certaines agences reçoivent leur premier lead le jour même !'
    },
    {
      question: 'Puis-je intégrer SmartImmo avec mon CRM existant ?',
      answer: 'Oui, avec notre formule Premium, nous proposons une intégration avec les principaux CRM immobiliers. Contactez-nous pour connaître les CRM supportés et les modalités d\'intégration.'
    },
    {
      question: 'Comment fonctionne la qualification des leads par IA ?',
      answer: 'Notre IA conversationnelle pose des questions pertinentes sur le projet immobilier, le budget, la timeline, etc. Elle attribue ensuite un score de qualité basé sur plus de 20 critères pour vous aider à prioriser vos leads.'
    },
    {
      question: 'Que se passe-t-il si je ne suis pas satisfait ?',
      answer: 'Il n\'y a aucun engagement. Vous pouvez arrêter d\'utiliser le service à tout moment. Nous sommes convaincus que vous constaterez rapidement la valeur de nos leads qualifiés.'
    },
    {
      question: 'Proposez-vous une formation pour utiliser la plateforme ?',
      answer: 'Oui, nous offrons une formation complète lors de votre inscription. De plus, notre équipe support est disponible pour répondre à toutes vos questions par email et téléphone.'
    },
    {
      question: 'Puis-je personnaliser les questions posées par l\'IA ?',
      answer: 'Oui, avec la formule Premium, vous pouvez personnaliser entièrement le ton et les questions de l\'IA pour qu\'elle corresponde parfaitement à votre image de marque.'
    },
    {
      question: 'Comment sont protégées les données des visiteurs ?',
      answer: 'Toutes les données sont cryptées et hébergées en France. Nous sommes 100% conformes au RGPD et prenons la protection des données très au sérieux.'
    },
    {
      question: 'Combien coûte l\'installation ?',
      answer: 'L\'installation est totalement gratuite ! Vous n\'avez qu\'à imprimer votre QR code personnalisé et l\'afficher dans votre agence. Aucun matériel supplémentaire n\'est nécessaire.'
    },
    {
      question: 'Offrez-vous un support technique ?',
      answer: 'Oui, nous offrons un support par email pour tous nos clients. Avec la formule Premium, vous bénéficiez d\'un support prioritaire avec un temps de réponse garanti sous 2h.'
    }
  ]

  return (
    <MainLayout>
      <Head title="Contact - SmartImmo">
        <meta name="description" content="Contactez-nous pour toute question sur SmartImmo. Notre équipe est là pour vous aider à transformer vos visiteurs en clients." />
        <meta property="og:title" content="Contact - SmartImmo" />
        <meta property="og:description" content="Une question ? Notre équipe est là pour vous aider." />
      </Head>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-20">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-600">
            Une question ? Notre équipe est là pour vous aider
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card>
              <Card.Body>
                <h2 className="text-2xl font-bold mb-6">Envoyez-nous un message</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    label="Nom complet"
                    type="text"
                    value={data.name}
                    onChange={(e) => setData('name', e.target.value)}
                    error={errors.name}
                    required
                    placeholder="Jean Dupont"
                  />

                  <Input
                    label="Email"
                    type="email"
                    value={data.email}
                    onChange={(e) => setData('email', e.target.value)}
                    error={errors.email}
                    required
                    placeholder="jean.dupont@example.com"
                  />

                  <Input
                    label="Téléphone"
                    type="tel"
                    value={data.phone}
                    onChange={(e) => setData('phone', e.target.value)}
                    error={errors.phone}
                    placeholder="06 12 34 56 78"
                  />

                  <Textarea
                    label="Message"
                    name="message"
                    value={data.message}
                    onChange={(e) => setData('message', e.target.value)}
                    rows={6}
                    required
                    error={errors.message}
                    placeholder="Comment pouvons-nous vous aider ?"
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    disabled={processing}
                    loading={processing}
                    className="w-full"
                  >
                    {processing ? 'Envoi en cours...' : 'Envoyer le message'}
                  </Button>
                </form>
              </Card.Body>
            </Card>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Autres moyens de nous joindre</h2>

              <div className="space-y-6">
                {/* Email */}
                <Card>
                  <Card.Body>
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary-100 text-primary-600">
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold mb-1">Email</h3>
                        <p className="text-gray-600 mb-2">Nous répondons sous 24h</p>
                        <a href="mailto:contact@smartimmo.fr" className="text-primary-600 hover:text-primary-700">
                          contact@smartimmo.fr
                        </a>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {/* Phone */}
                <Card>
                  <Card.Body>
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary-100 text-primary-600">
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold mb-1">Téléphone</h3>
                        <p className="text-gray-600 mb-2">Lun-Ven, 9h-18h</p>
                        <a href="tel:0123456789" className="text-primary-600 hover:text-primary-700">
                          01 23 45 67 89
                        </a>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {/* Support Premium */}
                <Card className="border-2 border-primary-200 bg-primary-50">
                  <Card.Body>
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary-600 text-white">
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold mb-1">Support Premium</h3>
                        <p className="text-gray-600 mb-2">Réponse sous 2h garantie</p>
                        <p className="text-sm text-gray-600">
                          Inclus avec la formule Premium à 149€/mois
                        </p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Questions fréquentes</h2>
            <p className="text-lg text-gray-600">
              Vous trouverez peut-être votre réponse ici
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <Card.Body>
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left flex justify-between items-start"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                    <svg
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${openFaq === index ? 'transform rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaq === index && (
                    <p className="mt-4 text-gray-600">{faq.answer}</p>
                  )}
                </Card.Body>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Vous ne trouvez pas votre réponse ?</p>
            <Button variant="outline" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              Contactez-nous directement
            </Button>
          </div>
        </div>
      </section>
    </MainLayout>
  )
}
